import { Injectable } from '@angular/core';

/**
 * Singleton service to prevent multiple initialization of Global Search across different projects.
 * This service ensures that Global Search is bootstrapped only once, regardless of how many
 * projects (casino, bingo, promo) attempt to initialize it.
 */
@Injectable({
    providedIn: 'root',
})
export class GlobalSearchBootstrapGuard {
    private static isBootstrapped = false;
    private static isBootstrapping = false;
    private static bootstrapPromise: Promise<void> | null = null;

    /**
     * Checks if Global Search has already been bootstrapped
     */
    isAlreadyBootstrapped(): boolean {
        return GlobalSearchBootstrapGuard.isBootstrapped;
    }

    /**
     * Checks if Global Search is currently in the process of bootstrapping
     */
    isCurrentlyBootstrapping(): boolean {
        return GlobalSearchBootstrapGuard.isBootstrapping;
    }

    /**
     * Attempts to acquire the bootstrap lock. Returns true if this call should proceed with bootstrapping,
     * false if another instance is already handling it.
     */
    tryAcquireBootstrapLock(): boolean {
        if (GlobalSearchBootstrapGuard.isBootstrapped || GlobalSearchBootstrapGuard.isBootstrapping) {
            return false;
        }
        GlobalSearchBootstrapGuard.isBootstrapping = true;
        return true;
    }

    /**
     * Marks the bootstrap process as completed
     */
    markBootstrapCompleted(): void {
        GlobalSearchBootstrapGuard.isBootstrapped = true;
        GlobalSearchBootstrapGuard.isBootstrapping = false;
    }

    /**
     * Marks the bootstrap process as failed and resets the state
     */
    markBootstrapFailed(): void {
        GlobalSearchBootstrapGuard.isBootstrapping = false;
        GlobalSearchBootstrapGuard.bootstrapPromise = null;
    }

    /**
     * Returns a promise that resolves when bootstrap is complete (either from this call or a previous one)
     */
    async waitForBootstrap(bootstrapFn: () => Promise<void>): Promise<void> {
        // If already bootstrapped, return immediately
        if (GlobalSearchBootstrapGuard.isBootstrapped) {
            return Promise.resolve();
        }

        // If currently bootstrapping, wait for the existing promise
        if (GlobalSearchBootstrapGuard.isBootstrapping && GlobalSearchBootstrapGuard.bootstrapPromise) {
            return GlobalSearchBootstrapGuard.bootstrapPromise;
        }

        // Try to acquire the lock
        if (!this.tryAcquireBootstrapLock()) {
            // Another instance is handling it, wait for the promise if it exists
            if (GlobalSearchBootstrapGuard.bootstrapPromise) {
                return GlobalSearchBootstrapGuard.bootstrapPromise;
            }
            // If no promise exists but we couldn't acquire lock, it means it's already done
            return Promise.resolve();
        }

        // We got the lock, create and store the bootstrap promise
        GlobalSearchBootstrapGuard.bootstrapPromise = this.executeBootstrap(bootstrapFn);
        return GlobalSearchBootstrapGuard.bootstrapPromise;
    }

    private async executeBootstrap(bootstrapFn: () => Promise<void>): Promise<void> {
        try {
            await bootstrapFn();
            this.markBootstrapCompleted();
        } catch (error) {
            this.markBootstrapFailed();
            throw error;
        }
    }

    /**
     * Reset the bootstrap state (useful for testing or forced re-initialization)
     */
    reset(): void {
        GlobalSearchBootstrapGuard.isBootstrapped = false;
        GlobalSearchBootstrapGuard.isBootstrapping = false;
        GlobalSearchBootstrapGuard.bootstrapPromise = null;
    }
}
